import type { RisClient } from '../../client';
import { type AssistantAction, type AssistantMessage, type Box, normaliseBox, normaliseMessage, type ViewportContext } from '../api';

/**
 * The ris routes of the Asistente tools (viewer-ai round 2 contract §4): region questions, assisted
 * measurement, voice/typed commands and lesion tracking. Every answer is normalised here, so the tools
 * never read a field they did not check.
 */

const AI_PREFIX = '/ris/v1/ai';

export interface Point01 {
  x: number;
  y: number;
}

export interface MeasureSuggestion {
  box: Box;
  longMm: number | null;
  shortMm: number | null;
  confidence: number | null;
}

export interface CommandAnswer {
  reply: string;
  actions: AssistantAction[];
}

export interface ViewerState {
  slice: number;
  count: number;
  series: string[];
  presets: string[];
}

export interface LesionLocation {
  series_uid: string;
  instance_uid: string;
  frame: number;
  index: number;
  box: Box | null;
}

export interface LesionMeasurement {
  id: string;
  studyUid: string;
  location: LesionLocation | null;
  longMm: number | null;
  shortMm: number | null;
  source: string;
  measuredAt: string | null;
}

export interface Lesion {
  id: string;
  label: string;
  measurements: LesionMeasurement[];
}

export interface NewLesion {
  label: string;
  location: LesionLocation;
  long_mm?: number;
  short_mm?: number;
}

export interface MatchResult {
  measurement: LesionMeasurement | null;
  longMmDelta: number | null;
  pct: number | null;
}

type Raw = Record<string, unknown>;

function isRecord(value: unknown): value is Raw {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function num(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function positive(value: unknown): number | null {
  const n = num(value);
  return n !== null && n > 0 ? n : null;
}

/** A measure answer with a usable box, or null (the viewer then says it found nothing). */
export function normaliseMeasure(value: unknown): MeasureSuggestion | null {
  if (!isRecord(value)) return null;
  const box = normaliseBox(value.box);
  if (!box) return null;
  return { box, longMm: positive(value.long_mm), shortMm: positive(value.short_mm), confidence: num(value.confidence) };
}

/** Command answer: the reply and the actions, read through the same normaliser as a message's. */
export function normaliseCommand(value: unknown): CommandAnswer {
  const raw = isRecord(value) ? value : {};
  const reply = typeof raw.reply === 'string' ? raw.reply : '';
  const message = normaliseMessage({ role: 'assistant', content: reply, actions: raw.actions });
  return { reply, actions: message?.actions ?? [] };
}

function normaliseLesionLocation(value: unknown): LesionLocation | null {
  if (!isRecord(value)) return null;
  const series = str(value.series_uid);
  const instance = str(value.instance_uid);
  const index = num(value.index);
  if (!series || !instance || index === null) return null;
  return { series_uid: series, instance_uid: instance, frame: Math.max(1, num(value.frame) ?? 1), index, box: normaliseBox(value.box) };
}

/** A measurement row: its address is either nested under `location` or flat on the row. */
export function normaliseLesionMeasurement(value: unknown): LesionMeasurement | null {
  if (!isRecord(value)) return null;
  const studyUid = str(value.study_uid);
  if (!studyUid) return null;
  return {
    id: String(value.id ?? ''),
    studyUid,
    location: normaliseLesionLocation(isRecord(value.location) ? { ...value.location } : value),
    longMm: positive(value.long_mm),
    shortMm: positive(value.short_mm),
    source: str(value.source) ?? 'manual',
    measuredAt: str(value.measured_at),
  };
}

export function normaliseLesion(value: unknown): Lesion | null {
  if (!isRecord(value)) return null;
  const id = value.id === undefined || value.id === null ? null : String(value.id);
  const label = str(value.label);
  if (!id || !label) return null;
  const rows = Array.isArray(value.measurements) ? value.measurements : [];
  return { id, label, measurements: rows.map(normaliseLesionMeasurement).filter((m): m is LesionMeasurement => m !== null) };
}

/** The list route answers an array, or `{items}` / `{lesions}`. */
export function normaliseLesions(value: unknown): Lesion[] {
  const rows = Array.isArray(value) ? value : isRecord(value) ? (value.items ?? value.lesions) : [];
  return Array.isArray(rows) ? rows.map(normaliseLesion).filter((l): l is Lesion => l !== null) : [];
}

export function normaliseMatch(value: unknown): MatchResult {
  const raw = isRecord(value) ? value : {};
  const change = isRecord(raw.change) ? raw.change : {};
  return { measurement: normaliseLesionMeasurement(raw.measurement), longMmDelta: num(change.long_mm_delta), pct: num(change.pct) };
}

function messages(value: unknown): AssistantMessage[] {
  return Array.isArray(value) ? value.map(normaliseMessage).filter((m): m is AssistantMessage => m !== null) : [];
}

const study = (uid: string) => `${AI_PREFIX}/studies/${encodeURIComponent(uid)}`;

export class ToolsApi {
  constructor(private readonly client: RisClient) {}

  /** A question about a box drawn on the viewport image (mode `region`): the question and its answer. */
  async askRegion(studyUid: string, body: { message: string; viewport: ViewportContext; box: Box }): Promise<AssistantMessage[]> {
    const payload = { message: body.message, mode: 'region', viewport: body.viewport, region: { box: body.box } };
    return messages(await this.client.call('POST', `${study(studyUid)}/assistant`, payload));
  }

  async measure(studyUid: string, viewport: ViewportContext, point: Point01): Promise<MeasureSuggestion | null> {
    return normaliseMeasure(await this.client.call('POST', `${study(studyUid)}/assistant/measure`, { viewport, point }));
  }

  async command(studyUid: string, text: string, viewerState: ViewerState): Promise<CommandAnswer> {
    return normaliseCommand(await this.client.call('POST', `${study(studyUid)}/assistant/command`, { text, viewer_state: viewerState }));
  }

  async lesions(studyUid: string): Promise<Lesion[]> {
    return normaliseLesions(await this.client.call('GET', `${study(studyUid)}/lesions`));
  }

  async createLesion(studyUid: string, body: NewLesion): Promise<Lesion | null> {
    return normaliseLesion(await this.client.call('POST', `${study(studyUid)}/lesions`, body));
  }

  async matchPrior(lesionId: string, priorStudyUid: string): Promise<MatchResult> {
    const path = `${AI_PREFIX}/lesions/${encodeURIComponent(lesionId)}/match-prior`;
    return normaliseMatch(await this.client.call('POST', path, { prior_study_uid: priorStudyUid }));
  }
}
